import Swiper from 'swiper';
import { A11y } from 'swiper/modules';
import { showToast } from '../common/site-shell';

function initDisclosures(root = document) {
    root.querySelectorAll('[data-disclosure-group]').forEach((group) => {
        const buttons = [...group.querySelectorAll('[data-disclosure-button]')];

        const setExpanded = (button, isOpen) => {
            const panel = group.querySelector(`#${CSS.escape(button.getAttribute('aria-controls') || '')}`);
            if (!panel) return;

            button.setAttribute('aria-expanded', String(isOpen));
            panel.hidden = !isOpen;
        };

        buttons.forEach((button, index) => {
            button.addEventListener('click', () => setExpanded(button, button.getAttribute('aria-expanded') !== 'true'));
            button.addEventListener('keydown', (event) => {
                let nextIndex = null;
                if (event.key === 'ArrowDown') nextIndex = (index + 1) % buttons.length;
                if (event.key === 'ArrowUp') nextIndex = (index - 1 + buttons.length) % buttons.length;
                if (event.key === 'Home') nextIndex = 0;
                if (event.key === 'End') nextIndex = buttons.length - 1;
                if (nextIndex === null) return;
                event.preventDefault();
                buttons[nextIndex]?.focus();
            });
        });
    });
}

function initWorkFilters(root = document) {
    root.querySelectorAll('[data-filter-tabs]').forEach((tabList) => {
        const tabs = [...tabList.querySelectorAll('[data-work-filter]')];
        const controlledId = tabs[0]?.getAttribute('aria-controls');
        const results = controlledId ? root.querySelector(`#${CSS.escape(controlledId)}`) : null;
        const cards = results ? [...results.querySelectorAll('[data-work-card]')] : [];
        const empty = root.querySelector('[data-work-filter-empty]');
        const count = root.querySelector('[data-work-result-count]');
        const announcement = root.querySelector('[data-work-result-announcement]');
        const search = root.querySelector('[data-work-search]');

        if (!tabs.length || !results) return;

        let currentFilter = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.workFilter || 'all';
        const apply = () => {
            const term = search?.value.trim().toLocaleLowerCase('ja-JP') || '';
            let visible = 0;

            cards.forEach((card) => {
                const category = card.dataset.category || '';
                const text = card.textContent.toLocaleLowerCase('ja-JP');
                const matchesFilter = currentFilter === 'all' || category === currentFilter;
                const matchesTerm = !term || text.includes(term);
                card.hidden = !(matchesFilter && matchesTerm);
                if (!card.hidden) visible += 1;
            });

            if (empty) empty.hidden = visible !== 0 || cards.length === 0;
            if (count) count.textContent = String(visible).padStart(2, '0');
            if (announcement) announcement.textContent = `${visible}件の作品を表示しています。`;
        };

        const select = (tab) => {
            currentFilter = tab.dataset.workFilter || 'all';
            tabs.forEach((item) => {
                const selected = item === tab;
                item.classList.toggle('is-active', selected);
                item.setAttribute('aria-selected', String(selected));
                item.tabIndex = selected ? 0 : -1;
            });
            results.setAttribute('aria-labelledby', tab.id);
            apply();
        };

        tabs.forEach((tab, index) => {
            tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
            tab.addEventListener('click', () => select(tab));
            tab.addEventListener('keydown', (event) => {
                let nextIndex = null;
                if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
                if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
                if (event.key === 'Home') nextIndex = 0;
                if (event.key === 'End') nextIndex = tabs.length - 1;
                if (nextIndex === null) return;
                event.preventDefault();
                select(tabs[nextIndex]);
                tabs[nextIndex].focus();
            });
        });

        search?.addEventListener('input', apply);
        apply();
    });
}

function initContactForm(root = document) {
    const form = root.querySelector('[data-contact-form]');
    if (!form) return;

    const error = form.querySelector('[data-contact-client-error]');
    const tokenField = form.querySelector('[data-recaptcha-token]');
    const idempotencyKeyField = form.querySelector('[data-contact-idempotency-key]');
    const submitButton = form.querySelector('[data-contact-submit]');
    const status = form.querySelector('[data-contact-client-status]');
    let challengeReady = false;
    let submissionStarted = false;

    const createUuid = () => {
        if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
        if (typeof window.crypto?.getRandomValues !== 'function') return null;

        const bytes = window.crypto.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    };
    const setSubmitting = (isSubmitting) => {
        if (!(submitButton instanceof HTMLButtonElement)) return;
        submitButton.disabled = isSubmitting;
        submitButton.setAttribute('aria-disabled', String(isSubmitting));
        form.setAttribute('aria-busy', String(isSubmitting));
        if (status) status.textContent = isSubmitting ? '送信準備中です。' : '';
    };
    const ensureIdempotencyKey = () => {
        if (!(idempotencyKeyField instanceof HTMLInputElement)) return false;
        if (idempotencyKeyField.value !== '') return true;

        const key = createUuid();
        if (!key) return false;
        idempotencyKeyField.value = key;
        return true;
    };
    const displayError = (message, input = null) => {
        if (!error) return;
        if (!error.id) error.id = 'contact-client-error';
        error.textContent = message;
        error.hidden = false;
        if (input instanceof HTMLElement) {
            input.setAttribute('aria-invalid', 'true');
            input.setAttribute('aria-errormessage', error.id);
        }
    };
    const waitForRecaptchaReady = () => new Promise((resolve, reject) => {
        let completed = false;
        const timeout = window.setTimeout(() => {
            if (completed) return;
            completed = true;
            reject(new Error('recaptcha_ready_timeout'));
        }, 10000);

        window.grecaptcha.ready(() => {
            if (completed) return;
            completed = true;
            window.clearTimeout(timeout);
            resolve();
        });
    });

    form.addEventListener('submit', async (event) => {
        if (challengeReady) {
            if (form.checkValidity()) return;

            event.preventDefault();
            challengeReady = false;
            submissionStarted = false;
            setSubmitting(false);
            form.reportValidity();
            return;
        }
        if (submissionStarted) {
            event.preventDefault();
            return;
        }

        error?.setAttribute('hidden', 'hidden');
        form.querySelectorAll('[aria-invalid="true"]').forEach((input) => {
            input.removeAttribute('aria-invalid');
            input.removeAttribute('aria-errormessage');
        });
        if (!form.checkValidity()) {
            event.preventDefault();
            form.reportValidity();
            return;
        }

        if (!ensureIdempotencyKey()) {
            event.preventDefault();
            displayError('送信の準備ができていません。時間をおいてもう一度お試しください。', form.querySelector('[name="email"]'));
            return;
        }

        submissionStarted = true;
        setSubmitting(true);

        if (!form.matches('[data-recaptcha-required]')) return;
        const siteKey = form.dataset.recaptchaSiteKey || '';
        if (!siteKey || !window.grecaptcha?.execute) {
            event.preventDefault();
            submissionStarted = false;
            setSubmitting(false);
            displayError('送信の準備ができていません。時間をおいてもう一度お試しください。', form.querySelector('[name="email"]'));
            return;
        }

        event.preventDefault();
        try {
            await waitForRecaptchaReady();
            const token = await window.grecaptcha.execute(siteKey, { action: 'contact_submit' });
            if (!token || !tokenField) throw new Error('token_unavailable');
            tokenField.value = token;
            challengeReady = true;
            form.requestSubmit();
        } catch {
            submissionStarted = false;
            setSubmitting(false);
            displayError('不正送信確認を完了できませんでした。送信せずに中止しました。', form.querySelector('[name="email"]'));
        }
    });
}

function initReveal(root = document) {
    const elements = [...root.querySelectorAll('[data-reveal]')];
    if (!elements.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        elements.forEach((element) => element.classList.add('is-visible'));
        return;
    }

    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
    }), { threshold: 0.08 });
    elements.forEach((element) => observer.observe(element));
}

function initWatchPlayer(root = document) {
    const player = root.querySelector('[data-watch-player]');
    if (!player) return;

    const swiperElement = player.querySelector('[data-watch-swiper]');
    const desktopPlayer = player.querySelector('[data-watch-desktop]');
    const mobileQuery = window.matchMedia('(max-width: 767px)');

    let queueMuted = true;
    let hasExplicitPlaybackStarted = false;
    let swiper = null;
    let playbackRequest = 0;
    let activeWorkUrl = window.location.pathname;
    const setFramePlaybackState = (video, isPlaying) => {
        const frame = video.closest('.video-frame');
        if (!frame) return;

        frame.classList.toggle('is-playing', isPlaying);
        const playButton = frame.querySelector('[data-watch-play]');
        if (playButton instanceof HTMLButtonElement) playButton.hidden = isPlaying;
    };
    const syncSoundState = () => {
        player.querySelectorAll('[data-watch-video]').forEach((video) => {
            video.muted = queueMuted;
        });
        player.querySelectorAll('[data-watch-sound]').forEach((button) => {
            button.setAttribute('aria-pressed', String(!queueMuted));
            button.textContent = queueMuted ? '音声オフ' : '音声オン';
        });
    };
    const pauseAllExcept = (activeVideo = null) => {
        player.querySelectorAll('[data-watch-video]').forEach((video) => {
            if (video !== activeVideo && !video.paused) video.pause();
        });
    };
    const startVideo = async (video, button = null, unmuteOnFirstExplicitPlay = false) => {
        const request = ++playbackRequest;
        if (unmuteOnFirstExplicitPlay && !hasExplicitPlaybackStarted) {
            queueMuted = false;
            hasExplicitPlaybackStarted = true;
            syncSoundState();
        }

        pauseAllExcept(video);
        video.muted = queueMuted;
        try {
            await video.play();
            if (request !== playbackRequest) {
                video.pause();
                return;
            }
            setFramePlaybackState(video, true);
            button?.setAttribute('aria-label', '一時停止する');
            video.closest('.video-frame')?.querySelector('[data-watch-status]')?.replaceChildren(document.createTextNode('再生中です。'));
        } catch {
            if (request !== playbackRequest) return;
            setFramePlaybackState(video, false);
            button?.setAttribute('aria-label', '再生を開始できませんでした。もう一度再生する');
            video.closest('.video-frame')?.querySelector('[data-watch-status]')?.replaceChildren(document.createTextNode('再生を開始できませんでした。'));
        }
    };
    const playFrame = async (frame) => {
        const video = frame?.querySelector?.('[data-watch-video]');
        if (!video) return;

        await startVideo(video, frame.querySelector('[data-watch-play]'));
    };
    const createElement = (name, text = '') => {
        const element = document.createElement(name);
        element.textContent = text;

        return element;
    };
    const workForSlide = (slide) => {
        const serializedWork = slide?.querySelector?.('[data-watch-work]')?.textContent || '';
        if (!serializedWork) return null;

        try {
            return JSON.parse(serializedWork);
        } catch {
            return null;
        }
    };
    const updateWorkInformation = (slide) => {
        const work = workForSlide(slide);
        if (!work) return null;

        const title = typeof work.title === 'string' ? work.title : '';
        const description = typeof work.description === 'string' ? work.description : '';
        const category = typeof work.category === 'string' ? work.category : '作品';
        const year = typeof work.year === 'string' || typeof work.year === 'number' ? String(work.year) : '';
        const infoCategory = root.querySelector('[data-watch-info-category]');
        const infoTitle = root.querySelector('[data-watch-info-title]');
        const infoDescription = root.querySelector('[data-watch-info-description]');
        const infoDetails = root.querySelector('[data-watch-info-details]');
        const credits = root.querySelector('[data-watch-info-credits]');
        const favoriteButton = root.querySelector('[data-favorite-toggle]');
        const playerLabel = player.querySelector('[data-watch-player-label]');

        if (infoCategory) infoCategory.textContent = `${category}${year ? ` / ${year}` : ''}`;
        if (infoTitle) {
            infoTitle.textContent = title;
            infoTitle.setAttribute('aria-label', title);
        }
        if (infoDescription) infoDescription.textContent = description;
        if (playerLabel) playerLabel.setAttribute('aria-label', `${title}の動画プレイヤー`);
        if (infoDetails) infoDetails.replaceChildren(
            document.createTextNode(`公開日　${typeof work.publishedAt === 'string' ? work.publishedAt : '—'}`),
            document.createElement('br'),
            document.createTextNode(`尺　${typeof work.duration === 'string' ? work.duration : '—'}`),
        );
        if (credits) {
            credits.replaceChildren();
            const items = Array.isArray(work.credits) ? work.credits : [];
            if (!items.length) {
                const row = createElement('div');
                row.className = 'credit-row';
                row.append(createElement('dt', 'クレジット'), createElement('dd', '公開情報を準備中です。'));
                credits.append(row);
            } else {
                items.forEach((credit) => {
                    const role = typeof credit?.role === 'string' ? credit.role : 'クレジット';
                    const name = typeof credit?.name === 'string' ? credit.name : '制作者';
                    const url = typeof credit?.url === 'string' ? credit.url : '/works';
                    const row = createElement('div');
                    row.className = 'credit-row';
                    const definition = createElement('dt', role);
                    const value = createElement('dd');
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('aria-label', `${name}のプロフィール`);
                    link.textContent = name;
                    value.append(link);
                    const arrow = document.createElement('a');
                    arrow.href = url;
                    arrow.setAttribute('aria-label', `${name}のプロフィールを開く`);
                    arrow.textContent = '↗';
                    row.append(definition, value, arrow);
                    credits.append(row);
                });
            }
        }
        if (favoriteButton instanceof HTMLButtonElement && work.videoId && work.favoriteUrl) {
            const isFavorite = work.isFavorite === true;
            favoriteButton.dataset.favoriteVideo = String(work.videoId);
            favoriteButton.dataset.favoriteUrl = String(work.favoriteUrl);
            favoriteButton.setAttribute('aria-pressed', String(isFavorite));
            const label = favoriteButton.querySelector('[data-favorite-label]');
            if (label) label.textContent = isFavorite ? '後で見るに保存済み' : '後で見るに保存';
        }
        if (title) document.title = `${title} | ta-bo`;
        if (typeof work.url === 'string' && work.url !== '') {
            activeWorkUrl = new URL(work.url, window.location.origin).pathname;
            if (window.location.pathname !== activeWorkUrl) window.history.replaceState({}, '', work.url);
        }
        return work;
    };
    const synchronizeDesktopPlayer = (slide) => {
        const work = workForSlide(slide);
        const desktopVideo = desktopPlayer?.querySelector('[data-watch-video]');
        const desktopSource = desktopVideo?.querySelector('source');
        const mobileVideo = slide?.querySelector?.('[data-watch-video]');
        if (!work || !(desktopVideo instanceof HTMLVideoElement) || !(desktopSource instanceof HTMLSourceElement) || typeof work.streamUrl !== 'string') {
            playFrame(desktopPlayer);
            return;
        }

        const streamUrl = new URL(work.streamUrl, window.location.origin).href;
        const resumeTime = Number.isFinite(mobileVideo?.currentTime) ? mobileVideo.currentTime : 0;
        const resume = () => {
            if (resumeTime > 0 && Number.isFinite(desktopVideo.duration)) desktopVideo.currentTime = resumeTime;
            startVideo(desktopVideo, desktopPlayer.querySelector('[data-watch-play]'));
        };
        if (desktopSource.src === streamUrl) {
            resume();
            return;
        }

        desktopVideo.pause();
        desktopSource.src = streamUrl;
        if (typeof work.posterUrl === 'string') desktopVideo.poster = work.posterUrl;
        desktopVideo.setAttribute('aria-label', typeof work.title === 'string' ? work.title : '作品を再生');
        desktopVideo.addEventListener('loadedmetadata', resume, { once: true });
        desktopVideo.load();
    };

    syncSoundState();
    const enableMobileSwiper = () => {
        if (!swiperElement || swiper || !mobileQuery.matches) return;

        const slides = [...swiperElement.querySelectorAll('[data-watch-mobile-slide]')];
        const matchingIndex = slides.findIndex((slide) => {
            const url = workForSlide(slide)?.url;

            return typeof url === 'string' && new URL(url, window.location.origin).pathname === activeWorkUrl;
        });
        swiper = new Swiper(swiperElement, {
            modules: [A11y],
            a11y: { enabled: true },
            initialSlide: matchingIndex >= 0 ? matchingIndex : 0,
            speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
        });
        swiper.on('slideChange', () => {
            updateWorkInformation(swiper.slides[swiper.activeIndex]);
            playFrame(swiper.slides[swiper.activeIndex]);
        });
        updateWorkInformation(swiper.slides[swiper.activeIndex]);
        playFrame(swiper.slides[swiper.activeIndex]);
    };
    const disableMobileSwiper = () => {
        if (!swiper) return;
        swiper.destroy(true, true);
        swiper = null;
    };
    const activateViewportPlayer = () => {
        const activeSlide = swiper?.slides[swiper.activeIndex] || null;
        pauseAllExcept();
        if (mobileQuery.matches) {
            enableMobileSwiper();
            return;
        }

        disableMobileSwiper();
        if (activeSlide) {
            synchronizeDesktopPlayer(activeSlide);
            return;
        }
        playFrame(desktopPlayer);
    };

    player.querySelectorAll('[data-watch-play]').forEach((button) => {
        button.addEventListener('click', async () => {
            const frame = button.closest('.video-frame');
            const video = frame?.querySelector('[data-watch-video]');
            if (!video) return;

            if (!hasExplicitPlaybackStarted && queueMuted) {
                await startVideo(video, button, true);
                return;
            }

            if (video.paused) {
                await startVideo(video, button, true);
                return;
            }

            video.pause();
            playbackRequest += 1;
            setFramePlaybackState(video, false);
            button.setAttribute('aria-label', '再生する');
        });
    });

    player.querySelectorAll('[data-watch-video]').forEach((video) => {
        video.addEventListener('click', async () => {
            const frame = video.closest('.video-frame');
            const playButton = frame?.querySelector('[data-watch-play]');
            if (!hasExplicitPlaybackStarted && queueMuted) {
                await startVideo(video, playButton, true);
                return;
            }

            if (video.paused) {
                await startVideo(video, playButton, true);
                return;
            }

            video.pause();
            playbackRequest += 1;
            setFramePlaybackState(video, false);
            playButton?.setAttribute('aria-label', '再生する');
        });
        video.addEventListener('play', () => setFramePlaybackState(video, true));
        video.addEventListener('pause', () => setFramePlaybackState(video, false));
        video.addEventListener('ended', () => {
            setFramePlaybackState(video, false);
            video.closest('.video-frame')?.querySelector('[data-watch-status]')?.replaceChildren(document.createTextNode('再生が終了しました。'));
        });
        video.addEventListener('timeupdate', () => {
            const frame = video.closest('.video-frame');
            const time = frame?.querySelector('[data-watch-time]');
            const progress = frame?.querySelector('[data-watch-progress]');
            const seconds = Number.isFinite(video.currentTime) ? Math.floor(video.currentTime) : 0;
            if (time) time.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
            if (progress && Number.isFinite(video.duration) && video.duration > 0) progress.style.width = `${(video.currentTime / video.duration) * 100}%`;
        });
    });

    player.querySelectorAll('[data-watch-sound]').forEach((button) => {
        button.addEventListener('click', () => {
            queueMuted = !queueMuted;
            hasExplicitPlaybackStarted = true;
            syncSoundState();
        });
    });

    root.querySelectorAll('[data-share-url]').forEach((button) => {
        button.addEventListener('click', async () => {
            try {
                if (!navigator.clipboard?.writeText) throw new Error('clipboard_unavailable');
                await navigator.clipboard.writeText(window.location.href);
                button.textContent = 'コピーしました';
            } catch {
                button.textContent = '共有リンクをコピーできませんでした';
            }
        });
    });

    if (typeof mobileQuery.addEventListener === 'function') {
        mobileQuery.addEventListener('change', activateViewportPlayer);
    } else {
        mobileQuery.addListener(activateViewportPlayer);
    }
    activateViewportPlayer();
}

function initFavorites(root = document) {
    const buttons = [...root.querySelectorAll('[data-favorite-toggle][data-favorite-url][data-favorite-video]')];
    if (!buttons.length) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    const controlsFor = (videoId) => buttons.filter((button) => button.dataset.favoriteVideo === String(videoId));
    const setPending = (videoId, pending) => {
        controlsFor(videoId).forEach((button) => {
            button.disabled = pending;
            button.setAttribute('aria-busy', String(pending));
        });
    };
    const update = (videoId, saved) => {
        controlsFor(videoId).forEach((button) => {
            button.setAttribute('aria-pressed', String(saved));
            const label = button.querySelector('[data-favorite-label]');
            if (label) label.textContent = saved ? '後で見るに保存済み' : '後で見るに保存';
            const title = button.getAttribute('aria-label');
            if (title) button.setAttribute('aria-label', title.replace(/後で見るに(?:保存済み|保存)/, `後で見るに${saved ? '保存済み' : '保存'}`));
        });
    };

    buttons.forEach((button) => {
        button.addEventListener('click', async () => {
            if (!(button instanceof HTMLButtonElement) || button.disabled) return;

            const videoId = button.dataset.favoriteVideo || '';
            const url = button.dataset.favoriteUrl || '';
            const previous = button.getAttribute('aria-pressed') === 'true';
            if (!videoId || !url) return;
            if (!csrfToken) {
                showToast('後で見るを更新できませんでした。ページを再読み込みしてもう一度お試しください。');
                return;
            }

            setPending(videoId, true);
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    body: '{}',
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok || typeof payload.saved !== 'boolean') {
                    throw new Error(typeof payload.message === 'string' ? payload.message : 'favorite_request_failed');
                }

                update(videoId, payload.saved);
                showToast(payload.saved ? '後で見るに保存しました。' : '後で見るから削除しました。');
            } catch (error) {
                update(videoId, previous);
                showToast(error instanceof Error && error.message !== 'favorite_request_failed'
                    ? error.message
                    : '後で見るを更新できませんでした。時間をおいてもう一度お試しください。');
            } finally {
                setPending(videoId, false);
            }
        });
    });
}

export function initPublicPages(root = document) {
    if (!root?.querySelector) return;
    initDisclosures(root);
    initWorkFilters(root);
    initContactForm(root);
    initReveal(root);
    initWatchPlayer(root);
    initFavorites(root);
}
